import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, ModifyUserOfSkill, CommonSkillService, SkillFilter, Skill, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, Client } from '@umanforge/common';
import { ModifySkill, PostSkill } from 'interfaces/api_skill.interfaces';
import { ImportError } from 'interfaces/api_import.interfaces';
import { isHeaderProblem } from 'utils/import.utils';


export class SkillService {

  static getSkill(
    skillId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Skill> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSkillService.getSkill( skillId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowSkills(
    filters: SkillFilter | null,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Skill[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSkillService.getAllowSkills( filters, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedSkillsBySitesId(
    sitesId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Skill[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSkillService.getAllowedSkillsBySitesId( sitesId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static postSkill(
    body: PostSkill,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( async resolve => {
      AXIOS.post( API.SKILLS, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 ? response.data.id : null );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

  static deleteSkill(
    skillId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SKILLS + skillId, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static putSkill(
    skillId: UUID, body: ModifySkill,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.SKILLS + skillId, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static postUsersToSkill(
    skillId: UUID, body: { users: ModifyUserOfSkill[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.SKILLS + skillId + '/users', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteUsersToSkill(
    skillId: UUID, body: { users: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SKILLS + skillId + '/users', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static exportSkillsOfClient(
    client: Client,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios,
    filters?: { filterSites?: UUID[] }
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SKILLS }export/${ client.id }`, {
        ...await CommonGlobalService.getHeaderRequest( token ),
        params: filters,
        responseType: 'blob'
      } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ client.name }_skills_export.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static downloadSkillsTemplate(
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SKILLS }export`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'skills_template.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static importSkillsOnClient(
    clientId: UUID, file: File, hasHeader: boolean | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID[] | ImportError | null> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( hasHeader !== undefined ) {
        formData.append( 'hasHeader', String( hasHeader ) );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.SKILLS }import/${ clientId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          let error: ImportError | null = null;
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && !( isHeaderProblem( response.data ) && hasHeader === undefined ) ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 202 ) {
            error = response.data as ImportError;
          }

          resolve( response.status === 200 ? response.data.ids : error );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

};